import { StyleSheet } from '@react-pdf/renderer'
import { PDF_COLORS } from 'features/reporting/pdfGeneration/constants/colors'
import { PDF_LAYOUT } from 'features/reporting/pdfGeneration/constants/layout'
import { PDF_TYPOGRAPHY } from 'features/reporting/pdfGeneration/constants/typography'

export const ELIGIBILITY_PDF_COLORS: Record<string, string> = {
  eligible: '#52c41a',
  not_eligible: '#ff4d4f',
  unclear: '#faad14',
}

export const NiiPdfStyles = StyleSheet.create({
  validityBanner: {
    padding: PDF_LAYOUT.SPACING.LARGE,
    marginBottom: PDF_LAYOUT.SPACING.LARGE,
    backgroundColor: PDF_COLORS.BACKGROUND_OFF_WHITE,
    borderWidth: PDF_LAYOUT.BORDER_WIDTH.NORMAL,
    borderColor: ELIGIBILITY_PDF_COLORS.unclear,
    borderRadius: PDF_LAYOUT.BORDER_RADIUS.SMALL,
  },

  validityText: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    color: PDF_COLORS.TEXT_PRIMARY,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.RELAXED,
  },

  signoffNote: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    color: PDF_COLORS.TEXT_PRIMARY,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.RELAXED,
    marginBottom: PDF_LAYOUT.SPACING.MEDIUM,
  },

  emptyStateText: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.ITALIC,
    color: PDF_COLORS.TEXT_SECONDARY,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.RELAXED,
  },

  blockGap: {
    marginTop: PDF_LAYOUT.SPACING.LARGE,
  },

  tableDivider: {
    borderTopWidth: PDF_LAYOUT.BORDER_WIDTH.NORMAL,
    borderTopColor: PDF_COLORS.BORDER_LIGHT,
  },

  arrayEntryDivider: {
    marginTop: PDF_LAYOUT.SPACING.MEDIUM,
    paddingTop: PDF_LAYOUT.SPACING.MEDIUM,
    borderTopWidth: PDF_LAYOUT.BORDER_WIDTH.THIN,
    borderTopColor: PDF_COLORS.BORDER_LIGHT,
  },

  zoneOverrides: {
    marginTop: PDF_LAYOUT.SPACING.LARGE,
    paddingTop: PDF_LAYOUT.SPACING.MEDIUM,
    borderTopWidth: PDF_LAYOUT.BORDER_WIDTH.NORMAL,
    borderTopColor: PDF_COLORS.BORDER_MEDIUM,
  },

  eligibilityTag: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },

  eligibilityTagText: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: 'bold',
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.BOLD,
    color: PDF_COLORS.TEXT_LIGHT,
  },

  recommendationItem: {
    flexDirection: 'row',
    marginBottom: PDF_LAYOUT.SPACING.XSMALL,
    paddingLeft: PDF_LAYOUT.SPACING.MEDIUM,
  },

  recommendationBullet: {
    width: 12,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    color: PDF_COLORS.TEXT_SECONDARY,
  },

  recommendationText: {
    flex: 1,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    color: PDF_COLORS.TEXT_PRIMARY,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.RELAXED,
  },

  preScreeningSummary: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    color: PDF_COLORS.TEXT_PRIMARY,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.RELAXED,
    marginBottom: PDF_LAYOUT.SPACING.MEDIUM,
  },

  preScreeningAnswerRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: PDF_LAYOUT.SPACING.MEDIUM,
    borderBottomWidth: PDF_LAYOUT.BORDER_WIDTH.THIN,
    borderBottomColor: PDF_COLORS.BORDER_LIGHT,
  },

  preScreeningAnswerTag: {
    width: 32,
    paddingVertical: PDF_LAYOUT.SPACING.XXSMALL,
    borderRadius: PDF_LAYOUT.BORDER_RADIUS.SMALL,
    alignItems: 'center',
  },

  preScreeningYesTag: {
    backgroundColor: ELIGIBILITY_PDF_COLORS.not_eligible,
  },

  preScreeningNoTag: {
    backgroundColor: PDF_COLORS.BACKGROUND_GRAY,
  },

  preScreeningYesText: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XSMALL,
    fontWeight: 'bold',
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.BOLD,
    color: PDF_COLORS.TEXT_LIGHT,
  },

  preScreeningNoText: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XSMALL,
    fontWeight: 'bold',
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.BOLD,
    color: PDF_COLORS.TEXT_SECONDARY,
  },

  preScreeningQuestion: {
    flex: 1,
    paddingLeft: PDF_LAYOUT.SPACING.MEDIUM,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    color: PDF_COLORS.TEXT_PRIMARY,
    lineHeight: PDF_TYPOGRAPHY.LINE_HEIGHT.RELAXED,
  },

  zoneBlock: {
    marginBottom: PDF_LAYOUT.SPACING.LARGE,
    padding: PDF_LAYOUT.SPACING.LARGE,
    backgroundColor: PDF_COLORS.BACKGROUND_LIGHT_GRAY,
    borderRadius: PDF_LAYOUT.BORDER_RADIUS.SMALL,
    borderWidth: PDF_LAYOUT.BORDER_WIDTH.THIN,
    borderColor: PDF_COLORS.BORDER_LIGHT,
  },

  zoneHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: PDF_LAYOUT.SPACING.MEDIUM,
  },

  zoneColorDot: {
    width: 10,
    height: 10,
    borderRadius: 3,
  },

  zoneTitle: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: 'bold',
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.BOLD,
    color: PDF_COLORS.TEXT_PRIMARY,
  },

  zoneImageBlock: {
    marginBottom: PDF_LAYOUT.SPACING.LARGE,
  },

  zoneImageLabel: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: 'bold',
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.BOLD,
    color: PDF_COLORS.TEXT_SECONDARY,
    marginBottom: PDF_LAYOUT.SPACING.SMALL,
  },

  zoneImage: {
    maxHeight: 400,
    objectFit: 'contain' as const,
  },

  subsectionTitle: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: 'bold',
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.BOLD,
    color: PDF_COLORS.TEXT_SECONDARY,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginTop: PDF_LAYOUT.SPACING.MEDIUM,
    marginBottom: PDF_LAYOUT.SPACING.SMALL,
  },

  strategyDecision: {
    marginTop: PDF_LAYOUT.SPACING.SMALL,
    padding: PDF_LAYOUT.SPACING.MEDIUM,
    backgroundColor: PDF_COLORS.BACKGROUND_LIGHT_GRAY,
    borderRadius: PDF_LAYOUT.BORDER_RADIUS.SMALL,
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    fontWeight: 'bold',
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.BOLD,
    color: PDF_COLORS.TEXT_PRIMARY,
    textAlign: 'center',
  },

  coverageResultPercent: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XLARGE,
    fontWeight: 'bold',
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.BOLD,
    color: PDF_COLORS.PRIMARY,
    textAlign: 'center',
    marginVertical: PDF_LAYOUT.SPACING.SMALL,
  },

  coverageResultSubtext: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.SMALL,
    color: PDF_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    marginBottom: PDF_LAYOUT.SPACING.MEDIUM,
  },

  coverageFormulaBlock: {
    backgroundColor: PDF_COLORS.BACKGROUND_LIGHT_GRAY,
    padding: PDF_LAYOUT.SPACING.MEDIUM,
    borderRadius: PDF_LAYOUT.BORDER_RADIUS.SMALL,
    marginBottom: PDF_LAYOUT.SPACING.SMALL,
  },

  coverageFormulaLine: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XSMALL,
    fontFamily: PDF_TYPOGRAPHY.FONT_FAMILY.PRIMARY,
    color: PDF_COLORS.TEXT_PRIMARY,
    marginBottom: 2,
  },

  coverageModifiers: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: PDF_LAYOUT.SPACING.SMALL,
  },

  coverageModifierTag: {
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 3,
    backgroundColor: PDF_COLORS.BACKGROUND_GRAY,
  },

  coverageModifierText: {
    fontSize: PDF_TYPOGRAPHY.FONT_SIZE.XSMALL,
    color: PDF_COLORS.TEXT_SECONDARY,
  },

  zoneAnalysisBlock: {
    marginBottom: PDF_LAYOUT.SPACING.XLARGE,
    padding: PDF_LAYOUT.SPACING.LARGE,
    backgroundColor: PDF_COLORS.BACKGROUND_LIGHT_GRAY,
    borderRadius: PDF_LAYOUT.BORDER_RADIUS.SMALL,
    borderWidth: PDF_LAYOUT.BORDER_WIDTH.THIN,
    borderColor: PDF_COLORS.BORDER_LIGHT,
  },
})
